/**
 * Structured-tab building blocks (spec §17.2): KeyValueRow, ObjectGroup,
 * ValueList, ProseBlock, JsonBlock, ChecksList, Findings, Tag.
 */
import { useState } from "react";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import FindInPageOutlined from "@mui/icons-material/FindInPageOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import EastRounded from "@mui/icons-material/EastRounded";

import {
  BREAKDOWN,
  FOCUS_RING,
  INK,
  LINE,
  LINE_SOFT,
  MONO,
  SUB,
  SURFACE,
  domId,
  flashSx,
  toneOf,
  toolsSx,
} from "./tokens";
import DataTable from "./DataTable";

// ── small pieces ─────────────────────────────────────────────────────────────
export const Tag = ({ label, tone = "neutral" }) => {
  const t = toneOf(tone);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: 0.875,
        borderRadius: "6px",
        fontSize: 11.5,
        fontWeight: 500,
        color: t.fg,
        backgroundColor: t.bg,
        whiteSpace: "nowrap",
        lineHeight: 1,
      }}
    >
      {label}
    </Box>
  );
};

export const LinkButton = ({ children, onClick, sx, ...rest }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      fontSize: 12,
      fontWeight: 500,
      color: SUB,
      borderRadius: "4px",
      gap: 0.25,
      "&:hover": { color: INK },
      "&:focus-visible": FOCUS_RING,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </ButtonBase>
);

const ToneIcon = ({ tone, size = 18 }) => {
  const t = toneOf(tone);
  const Icon = tone === "ok" ? CheckRounded : tone === "crit" ? CloseRounded : PriorityHighRounded;
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: "50%",
        backgroundColor: t.bg,
        color: t.solid,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: size * 0.7 }} />
    </Box>
  );
};

const noteColor = (tone) => (tone === "muted" || !tone ? SUB : toneOf(tone).fg);

const ToolButton = ({ title, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{ p: 0.5, color: SUB, "&:hover": { color: INK }, "&:focus-visible": FOCUS_RING }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

// ── KeyValueRow ──────────────────────────────────────────────────────────────
export function KeyValueRow({ row, ui, actions }) {
  const { enqueueSnackbar } = useSnackbar();
  const [partsOpen, setPartsOpen] = useState(false);
  const revealed = ui.revealed.has(row.path);
  const masked = row.sensitive && !revealed;
  const display = masked ? row.sensitive.masked : row.display;
  const flash = ui.flash?.path === row.path ? ui.flash.mode : undefined;

  const copy = () => {
    navigator.clipboard?.writeText(String(row.copyValue ?? row.display ?? ""));
    enqueueSnackbar("Copied", { variant: "success" });
  };

  return (
    <Box
      id={domId(row.path)}
      data-flash={flash}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 34%) 1fr auto",
        columnGap: 1.5,
        alignItems: "start",
        px: 1,
        py: 0.875,
        borderRadius: "7px",
        "&:hover": { backgroundColor: SURFACE },
        ...toolsSx,
        ...flashSx,
      }}
    >
      <Box sx={{ fontSize: 12.5, color: SUB, pt: "1px", overflowWrap: "anywhere" }}>{row.label}</Box>
      <Box sx={{ minWidth: 0 }}>
        {row.empty ? (
          <Box sx={{ fontSize: 13, color: SUB }}>Not found</Box>
        ) : (
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: row.tone && row.format === "enum" ? toneOf(row.tone).fg : INK,
              fontVariantNumeric: "tabular-nums",
              fontFamily: row.format === "id" || masked ? MONO : undefined,
              overflowWrap: "anywhere",
            }}
          >
            {display}
          </Box>
        )}
        {row.note ? (
          <Box sx={{ fontSize: 12, mt: 0.25, color: noteColor(row.note.tone) }}>{row.note.text}</Box>
        ) : null}
        {row.breakdown ? <Breakdown items={row.breakdown} /> : null}
        {row.parts ? (
          <>
            <LinkButton onClick={() => setPartsOpen((v) => !v)} sx={{ mt: 0.5 }} aria-expanded={partsOpen}>
              <KeyboardArrowRightRounded
                sx={{ fontSize: 15, transition: "transform .15s", transform: partsOpen ? "rotate(90deg)" : "none" }}
              />
              {partsOpen ? "Hide parts" : "Show parts"}
            </LinkButton>
            {partsOpen ? (
              <Box
                component="dl"
                sx={{
                  m: 0,
                  mt: 0.5,
                  pl: 1.5,
                  borderLeft: `2px solid ${LINE}`,
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 1.5,
                  rowGap: 0.25,
                  fontSize: 12,
                }}
              >
                {row.parts.map((p) => (
                  <Box key={p.path} sx={{ display: "contents" }}>
                    <Box component="dt" sx={{ color: SUB }}>{p.label}</Box>
                    <Box
                      component="dd"
                      sx={{ m: 0, color: p.display ? INK : p.missing ? toneOf("warn").fg : SUB }}
                    >
                      {p.display || (p.missing ? "Not provided" : "Not found")}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
      <Box className="sv-tools" sx={{ display: "flex", alignItems: "center", mt: "-3px" }}>
        {!row.empty && row.sensitive ? (
          <ToolButton
            title={revealed ? `Hide ${row.label}` : `Reveal ${row.label}`}
            onClick={() => actions.toggleReveal(row.path)}
          >
            {revealed ? <VisibilityOffOutlined sx={{ fontSize: 14 }} /> : <VisibilityOutlined sx={{ fontSize: 14 }} />}
          </ToolButton>
        ) : null}
        {!row.empty && ui.canShowInText && row.findQuery && actions.showInText ? (
          <ToolButton title={`Show ${row.label} in text`} onClick={() => actions.showInText(row.findQuery)}>
            <FindInPageOutlined sx={{ fontSize: 14 }} />
          </ToolButton>
        ) : null}
        {!row.empty && !masked ? (
          <ToolButton title={`Copy ${row.label}`} onClick={copy}>
            <ContentCopyOutlined sx={{ fontSize: 13 }} />
          </ToolButton>
        ) : null}
      </Box>
    </Box>
  );
}

const Breakdown = ({ items }) => {
  const total = items.reduce((n, b) => n + Math.max(0, b.value), 0) || 1;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: "flex", gap: "2px", height: 8, borderRadius: "999px", overflow: "hidden" }}>
        {items.map((b) => (
          <Box
            key={b.label}
            sx={{ flex: `${Math.max(0, b.value) / total} 1 0`, minWidth: 3, backgroundColor: BREAKDOWN[b.tone] }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, mt: 0.625 }}>
        {items.map((b) => (
          <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 0.625, fontSize: 12, color: SUB }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: BREAKDOWN[b.tone] }} />
            {b.label}
            <Box component="span" sx={{ fontWeight: 600, color: INK }}>{b.display}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── ObjectGroup ──────────────────────────────────────────────────────────────
export function ObjectGroup({ block, ui, actions, hideEmpty }) {
  const open = block.collapsedByDefault ? ui.openObjects.has(block.path) : !ui.closedObjects.has(block.path);
  return (
    <Box id={domId(block.path)} sx={{ mt: 1, mb: 0.5 }}>
      <ButtonBase
        onClick={() => actions.toggleObject(block.path, block.collapsedByDefault)}
        aria-expanded={open}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: "4px",
          fontSize: 12.5,
          fontWeight: 600,
          color: INK,
          "&:focus-visible": FOCUS_RING,
        }}
      >
        <KeyboardArrowRightRounded
          sx={{ fontSize: 16, color: SUB, transition: "transform .15s", transform: open ? "rotate(90deg)" : "none" }}
        />
        {block.label}
        {!open && block.summary ? (
          <Box component="span" sx={{ fontWeight: 400, color: SUB, ml: 0.75 }}>
            {block.summary}
          </Box>
        ) : null}
        {!open ? (
          <Box component="span" sx={{ fontWeight: 400, color: SUB, ml: 0.75 }}>
            Show {block.fieldCount ?? block.blocks.length} fields
          </Box>
        ) : null}
      </ButtonBase>
      {open ? (
        <Box sx={{ ml: 1.5, pl: 0.5, borderLeft: `2px solid ${LINE_SOFT}` }}>
          <Blocks blocks={block.blocks} ui={ui} actions={actions} hideEmpty={hideEmpty} />
        </Box>
      ) : null}
    </Box>
  );
}

// ── ValueList ────────────────────────────────────────────────────────────────
export function ValueList({ block, ui }) {
  const [all, setAll] = useState(false);
  const items = all ? block.items : block.items.slice(0, 8);
  const flash = ui.flash?.path === block.path ? ui.flash.mode : undefined;
  return (
    <Box
      id={domId(block.path)}
      data-flash={flash}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 34%) 1fr",
        columnGap: 1.5,
        px: 1,
        py: 0.875,
        borderRadius: "7px",
        ...flashSx,
      }}
    >
      <Box sx={{ fontSize: 12.5, color: SUB }}>{block.label}</Box>
      <Box>
        {block.presentation === "chips" ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {items.map((it) => (
              <Box
                key={it.path}
                component="span"
                sx={{
                  fontSize: 11.5,
                  px: 0.875,
                  py: "2px",
                  borderRadius: "6px",
                  border: `1px solid ${it.tone ? toneOf(it.tone).bg : LINE}`,
                  backgroundColor: it.tone ? toneOf(it.tone).bg : "transparent",
                  color: it.tone ? toneOf(it.tone).fg : INK,
                }}
              >
                {it.display}
              </Box>
            ))}
          </Box>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 13, color: INK }}>
            {items.map((it) => (
              <li key={it.path}>{it.display}</li>
            ))}
          </Box>
        )}
        {block.items.length > 8 ? (
          <LinkButton onClick={() => setAll((v) => !v)} sx={{ mt: 0.5 }}>
            {all ? "Show less" : `Show all ${block.items.length}`}
          </LinkButton>
        ) : null}
      </Box>
    </Box>
  );
}

// ── ProseBlock ───────────────────────────────────────────────────────────────
export const Clamped = ({ text, lines, moreLabel = "Read more", sx }) => {
  const [open, setOpen] = useState(false);
  const long = String(text).length > lines * 90;
  return (
    <Box>
      <Box
        sx={{
          fontSize: 13,
          lineHeight: 1.55,
          color: INK,
          whiteSpace: "pre-wrap",
          ...(long && !open
            ? { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }
            : null),
          ...sx,
        }}
      >
        {text}
      </Box>
      {long ? (
        <LinkButton onClick={() => setOpen((v) => !v)} sx={{ mt: 0.5 }} aria-expanded={open}>
          {open ? "Show less" : moreLabel}
        </LinkButton>
      ) : null}
    </Box>
  );
};

export function ProseBlock({ block, ui, showLabel }) {
  const flash = ui.flash?.path === block.path ? ui.flash.mode : undefined;
  return (
    <Box
      id={domId(block.path)}
      data-flash={flash}
      sx={{
        px: 1,
        py: 0.875,
        borderRadius: "7px",
        ...(block.tone === "crit"
          ? { borderLeft: `3px solid ${toneOf("crit").solid}`, borderRadius: 0, pl: 1.25, backgroundColor: toneOf("crit").bg }
          : null),
        ...flashSx,
      }}
    >
      {showLabel ? <Box sx={{ fontSize: 12.5, color: SUB, mb: 0.5 }}>{block.label}</Box> : null}
      <Clamped text={block.text} lines={block.clampLines || 4} />
    </Box>
  );
}

// ── JsonBlock ────────────────────────────────────────────────────────────────
export function JsonBlock({ block }) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const lines = block.json.split("\n");
  const long = lines.length > 12;
  return (
    <Box id={domId(block.path)} sx={{ px: 1, py: 0.875 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Box sx={{ fontSize: 12.5, color: SUB, flex: 1 }}>{block.label}</Box>
        <ToolButton
          title={`Copy ${block.label}`}
          onClick={() => {
            navigator.clipboard?.writeText(block.json);
            enqueueSnackbar("Copied", { variant: "success" });
          }}
        >
          <ContentCopyOutlined sx={{ fontSize: 13 }} />
        </ToolButton>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          fontFamily: MONO,
          fontSize: 11.5,
          lineHeight: 1.5,
          color: INK,
          backgroundColor: SURFACE,
          border: `1px solid ${LINE}`,
          borderRadius: "6px",
          overflowX: "auto",
          whiteSpace: "pre",
        }}
      >
        {long && !open ? lines.slice(0, 12).join("\n") : block.json}
      </Box>
      {long ? (
        <LinkButton onClick={() => setOpen((v) => !v)} sx={{ mt: 0.5 }}>
          {open ? "Show less" : `Show all ${lines.length} lines`}
        </LinkButton>
      ) : null}
    </Box>
  );
}

// ── ChecksList ───────────────────────────────────────────────────────────────
export function ChecksList({ checks, onGo }) {
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "10px", overflow: "hidden" }}>
      {checks.map((c, i) => {
        const interactive = !!c.target;
        const inner = (
          <>
            <ToneIcon tone={c.tone} />
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <Box sx={{ fontSize: 13, fontWeight: 500, color: INK }}>{c.title}</Box>
              {c.detail ? <Box sx={{ fontSize: 12, color: SUB, mt: 0.25 }}>{c.detail}</Box> : null}
            </Box>
            {interactive ? (
              <EastRounded className="sv-arrow" sx={{ fontSize: 15, color: SUB, opacity: 0, transition: "opacity .12s" }} />
            ) : null}
          </>
        );
        const rowSx = {
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          width: "100%",
          px: 1.5,
          py: 1.125,
          borderTop: i ? `1px solid ${LINE_SOFT}` : "none",
        };
        return interactive ? (
          <ButtonBase
            key={c.id}
            onClick={() => onGo(c.target)}
            sx={{
              ...rowSx,
              "&:hover": { backgroundColor: SURFACE },
              "&:hover .sv-arrow, &:focus-visible .sv-arrow": { opacity: 1 },
              "&:focus-visible": { ...FOCUS_RING, outlineOffset: "-2px" },
            }}
          >
            {inner}
          </ButtonBase>
        ) : (
          <Box key={c.id} sx={rowSx}>
            {inner}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Findings ─────────────────────────────────────────────────────────────────
export function Findings({ block, onGo }) {
  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      {block.summary ? (
        <Box sx={{ mb: 1.25 }}>
          <Clamped text={block.summary} lines={3} moreLabel="Read full summary" />
        </Box>
      ) : null}
      {block.intro ? <Box sx={{ fontSize: 12, color: SUB, mb: 0.75 }}>{block.intro}</Box> : null}
      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "10px", overflow: "hidden" }}>
        {block.findings.map((f, i) => (
          <Box
            key={f.id}
            sx={{
              display: "flex",
              gap: 1.25,
              px: 1.5,
              py: 1.125,
              borderTop: i ? `1px solid ${LINE_SOFT}` : "none",
            }}
          >
            <ToneIcon tone={f.tone} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 13, lineHeight: 1.5, color: INK }}>
                <Box component="span" sx={{ fontWeight: 600 }}>{f.headline}</Box>
                {f.body ? <Box component="span">{` ${f.body}`}</Box> : null}
              </Box>
              {f.refs.length ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                  {f.refs.map((r) => (
                    <ButtonBase
                      key={r.path}
                      onClick={() => onGo({ path: r.path, expandGroup: r.groupId })}
                      sx={{
                        gap: 0.625,
                        fontSize: 11.5,
                        px: 0.875,
                        py: "2px",
                        borderRadius: "6px",
                        border: `1px solid ${LINE}`,
                        color: INK,
                        "&:hover": { backgroundColor: SURFACE },
                        "&:focus-visible": FOCUS_RING,
                      }}
                    >
                      {r.label}
                      <Box component="span" sx={{ color: SUB }}>{r.display}</Box>
                    </ButtonBase>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
      {block.outro ? <Box sx={{ fontSize: 12, color: SUB, mt: 0.75 }}>{block.outro}</Box> : null}
    </Box>
  );
}

// ── dispatcher ───────────────────────────────────────────────────────────────
export function Blocks({ blocks, ui, actions, hideEmpty }) {
  return blocks.map((b) => {
    if (b.kind === "kv") {
      if (hideEmpty && b.empty) return null;
      return <KeyValueRow key={b.path} row={b} ui={ui} actions={actions} />;
    }
    if (b.kind === "object") return <ObjectGroup key={b.path} block={b} ui={ui} actions={actions} hideEmpty={hideEmpty} />;
    if (b.kind === "list") return <ValueList key={b.path} block={b} ui={ui} />;
    if (b.kind === "prose") return <ProseBlock key={b.path} block={b} ui={ui} showLabel={blocks.length > 1} />;
    if (b.kind === "json") return <JsonBlock key={b.path} block={b} />;
    if (b.kind === "findings") return <Findings key={b.path} block={b} onGo={actions.goTo} />;
    if (b.kind === "table") return <DataTable key={b.path} table={b} ui={ui} actions={actions} />;
    return null;
  });
}
